import axios from "axios";
import { localStorageItemNames } from "../constants";

export const MODULE_ACTIVITY_ACTION_EVENT = "cashflow:module-activity-action";

const getMutationActivity = (url, method) => {
  if (!url) return null;

  const path = new URL(url, window.location.origin).pathname;
  const segments = path.split("/").filter(Boolean);
  const endpoint = (segments[segments.length - 1] || "").toLowerCase();
  const httpMethod = (method || "").toLowerCase();
  let action;
  let resource;

  if (httpMethod === "delete" || /(delete|remove)/.test(endpoint)) {
    action = "deleted";
    resource = httpMethod === "delete"
      ? segments[segments.length - 1]
      : segments[segments.length - 2];
  } else if (/(create|add|insert)/.test(endpoint)) {
    action = "added";
    resource = segments[segments.length - 2];
  } else if (/(update|edit)/.test(endpoint)) {
    action = "updated";
    resource = segments[segments.length - 2];
  } else if (/upload/.test(endpoint)) {
    action = "uploaded";
    resource = segments[segments.length - 2];
  }

  if (!action || !resource) return null;

  const labels = {
    analysisresults: "Analysis Result",
    dealercompany: "Dealer Company",
    fuelanalysis: "Fuel Analysis",
    fueleconomy: "Fuel Economy",
    fundingtype: "Funding Type",
    helpdesk: "Help Desk",
    maintenance: "Maintenance",
    newvehicle: "New Vehicle",
    replacementschedule: "Replacement Schedule",
    sortablecolumn: "Sortable Column",
    userlogs: "User Log",
    users: "User",
  };
  const normalizedResource = resource.toLowerCase();
  const resourceName =
    labels[normalizedResource] ||
    resource
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      .replace(/ies$/i, "y")
      .replace(/s$/i, "")
      .trim();

  return `${resourceName} ${action}`;
};

const moduleActivityInterceptorKey = Symbol.for(
  "cashflow.moduleActivityInterceptorInstalled",
);
const moduleActivityResponseKey = Symbol.for(
  "cashflow.moduleActivityResponseLogged",
);

if (typeof window !== "undefined" && !window[moduleActivityInterceptorKey]) {
  window[moduleActivityInterceptorKey] = true;
  axios.interceptors.response.use((response) => {
    const config = response?.config;
    if (
      config &&
      !config[moduleActivityResponseKey] &&
      response?.data?.succeeded !== false
    ) {
      config[moduleActivityResponseKey] = true;
      const activityTitle = getMutationActivity(
        config.url,
        config.method,
      );
      if (activityTitle && !/\buser\s+log\b/i.test(activityTitle)) {
        window.dispatchEvent(
          new CustomEvent(MODULE_ACTIVITY_ACTION_EVENT, {
            detail: { activityTitle },
          }),
        );
      }
    }

    return response;
  });
}

export const getData = (urlToGetData) => {
  return resolveApiCall(axios.get(urlToGetData, getRequestConfiguration()));
};

export const postData = (urlToPostData, data) => {
  return resolveApiCall(
    axios.post(urlToPostData, data, getRequestConfiguration())
  );
};

export const postFileData = (urlToPostData, data) => {
  return resolveApiCall(axios.post(urlToPostData, data, getFileRequest()));
};

export const postFormData = (urlToPostData, data) => {
  return resolveApiCall(
    axios.post(urlToPostData, data, getFormRequestConfiguration())
  );
};

export const postMultiPartFormData = (urlToPostData, data) => {
  return resolveApiCall(
    axios.post(urlToPostData, data, getFormMultiPartConfiguration())
  );
};

export const putData = (urlToPutData, data) => {
  return resolveApiCall(
    axios.put(urlToPutData, data, getRequestConfiguration())
  );
};

export const deleteData = (urlToDeleteData) => {
  return resolveApiCall(
    axios.delete(urlToDeleteData, getRequestConfiguration())
  );
};

const resolveApiCall = (apiCall) => {
  return new Promise((resolve) => {
    const apiResult = {};
    apiCall
      .then((response) => {
        apiResult.data = response && response.data ? response.data : undefined;
        resolve(apiResult);
      })
      .catch((error) => {
        apiResult.error = error;
        resolve(apiResult);
      });
  });
};

const getRequestConfiguration = () => {
  const axiosRequestConfig = {};
  axiosRequestConfig.headers = getHeaders("json");
  return axiosRequestConfig;
};

const getFormRequestConfiguration = () => {
  const axiosRequestConfig = {};
  axiosRequestConfig.headers = getHeaders("form");
  return axiosRequestConfig;
};

const getFormMultiPartConfiguration = () => {
  const axiosRequestConfig = {};
  axiosRequestConfig.headers = getHeaders("form-data");
  return axiosRequestConfig;
};
const getHeaders = (type) => {
  const headers = {};

  switch (type) {
    case "form":
      headers["Content-Type"] = "application/x-www-form-urlencoded";
      break;
    case "form-data":
      headers["Content-Type"] = "multipart/form-data";
      break;

    default:
      headers["Content-Type"] = "application/json";
      break;
  }

  const token = getToken();
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  return headers;
};

const getToken = () => {
  const token = localStorage.getItem(localStorageItemNames.user_token);
  return token;
};

const getFileRequest = () => {
  const axiosRequestConfig = getRequestConfiguration();
  axiosRequestConfig.responseType = "arraybuffer"; // blob also can be possible
  return axiosRequestConfig;
};
